import { useAuth } from '@clerk/clerk-react'
import { useCallback } from 'react'

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1'

interface ApiError extends Error {
  status?: number
  issues?: Record<string, string[]>
}

export async function apiClient<T>(
  endpoint: string,
  options: RequestInit & { token?: string } = {}
): Promise<T> {
  const { token, ...fetchOptions } = options

  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...fetchOptions.headers,
    },
  })

  let data: any
  try {
    data = await res.json()
  } catch {
    const error: ApiError = new Error(`Server error (${res.status})`)
    error.status = res.status
    throw error
  }

  if (!data.success) {
    const error: ApiError = new Error(data.error || 'Request failed')
    error.status = res.status
    error.issues = data.issues
    throw error
  }

  return data.data
}

/**
 * Hook that returns an API client function with Clerk auth token injection
 */
export function useApiClient() {
  const { getToken } = useAuth()

  return useCallback(
    async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
      const token = await getToken()
      return apiClient<T>(endpoint, { ...options, token: token ?? undefined })
    },
    [getToken]
  )
}

/**
 * Helper to create mutation options for React Query
 */
export function createMutationFn<TData, TVariables>(
  endpoint: string | ((variables: TVariables) => string),
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST'
) {
  return async (variables: TVariables, token: string | null): Promise<TData> => {
    const url = typeof endpoint === 'function' ? endpoint(variables) : endpoint
    return apiClient<TData>(url, {
      method,
      body: JSON.stringify(variables),
      token: token ?? undefined,
    })
  }
}
